import { useCallback } from 'react';

export type DataTableAutoResetTrigger = 'filter' | 'sort' | 'pageIndex' | 'pageSize';

export type DataTableAutoResetOptions = {
  /**
   * 행 확장 자동 초기화 설정
   * true: 모든 변화에 행 확장 초기화
   * 객체: 지정된 변화에 대한 행 확장 초기화 여부 설정
   *
   * @default true
   */
  autoResetRowExpanded?: DataTableAutoResetTriggerOption;
  /**
   * 행 선택 자동 초기화 설정
   * true: 모든 변화에 행 선택 초기화
   * 객체: 지정된 변화에 대한 행 선택 초기화 여부 설정
   *
   * @default true
   */
  autoResetRowSelection?: DataTableAutoResetTriggerOption;
};

export type DataTableAutoResetTriggerOption =
  | boolean
  | Partial<Record<DataTableAutoResetTrigger, boolean>>;

export interface UseDataTableAutoResetHandlersParams {
  context: {
    resetRowExpanded: () => void;
    resetRowSelection: () => void;
  };
  autoReset?: DataTableAutoResetOptions;
}

const DEFAULT_AUTO_RESET_ROW_EXPANDED: DataTableAutoResetTriggerOption = true;
const DEFAULT_AUTO_RESET_ROW_SELECTION: DataTableAutoResetTriggerOption = true;

const useDataTableAutoResetHandlers = (params: UseDataTableAutoResetHandlersParams) => {
  const { autoReset, context } = params;

  const { resetRowExpanded, resetRowSelection } = context;

  const { autoResetRowExpanded, autoResetRowSelection } = autoReset ?? {};

  // 자동 초기화 헬퍼 함수들
  const shouldAutoReset = useCallback(
    (
      option: DataTableAutoResetTriggerOption | undefined = {},
      defaultOption: DataTableAutoResetTriggerOption,
      trigger: DataTableAutoResetTrigger,
    ): boolean => {
      if (typeof option === 'boolean') return option;
      if (option[trigger] !== undefined) return option[trigger];
      if (typeof defaultOption === 'boolean') return defaultOption;
      return !!defaultOption[trigger];
    },
    [],
  );

  /**
   * autoResetRowSelection 옵션에 `trigger`에 해당하는 값이 포함된 경우 행 선택을 초기화합니다.
   */
  const resetRowSelectionIfNeeded = useCallback(
    (trigger: DataTableAutoResetTrigger) => {
      if (shouldAutoReset(autoResetRowSelection, DEFAULT_AUTO_RESET_ROW_SELECTION, trigger)) {
        resetRowSelection();
      }
    },
    [shouldAutoReset, autoResetRowSelection, resetRowSelection],
  );

  /**
   * autoResetRowExpanded 옵션에 `trigger`에 해당하는 값이 포함된 경우 행 확장을 초기화합니다.
   */
  const resetRowExpandedIfNeeded = useCallback(
    (trigger: DataTableAutoResetTrigger) => {
      if (shouldAutoReset(autoResetRowExpanded, DEFAULT_AUTO_RESET_ROW_EXPANDED, trigger)) {
        resetRowExpanded();
      }
    },
    [shouldAutoReset, autoResetRowExpanded, resetRowExpanded],
  );

  return {
    resetRowExpandedIfNeeded,
    resetRowSelectionIfNeeded,
  };
};

export default useDataTableAutoResetHandlers;
